import clsx from "clsx";
import { AlertTriangle, CheckCircle2, Compass, Drone, Flame, Loader2, Play, RotateCcw, Sparkles } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import { streamWargame } from "../../api/client";
import type { CoaScenario, DroneRegime, Phase, WargameAssessment, WargameDrones, WargameSimulation } from "../../api/types";
import { Callout } from "../../components/Callout";
import { ChoiceCard, StatTile } from "../../components/panel";
import { motion, Presence } from "../../components/presence";
import { SidePanel } from "../../components/SidePanel";
import { Badge, Button, Chip, SectionLabel } from "../../components/ui";
import { smallDronesEffect } from "../../drones";
import { unitDesignation } from "../../i18n/designation";
import { k, locale, t, tServer } from "../../i18n/i18n";
import { unitMissionName } from "../../i18n/vocabulary";
import { plural } from "../../plural";
import { onRadioGroupKeyDown, rovingTabIndex } from "../radioGroupKeys";
import type { SketchPhase } from "./CoaSketch";
import { SEVERITY_COLOR, SEVERITY_LABEL, SEVERITY_ORDER } from "./severity";
import { Waiting } from "../../components/Waiting";

/** Wargame MANFUT (popup « MANFUT » de l'iPad) : confrontation de la manœuvre amie, temps par temps, au mode
 *  d'action ennemi choisi. Contacts, sévérités, feux, rapport de forces et contrôles du dispositif sont calculés
 *  par le serveur ; le modèle de langage rédige ensuite l'évaluation, affichée au fil de l'eau (registre D4 à D6).
 *  Volet latéral partagé (`SidePanel`, 440 px comme l'éditeur de l'OPO et le volet de la DIREX) : en ligne à partir de 1280 px, en surimpression avec voile en
 *  dessous, pour que les cartes ME gardent leur largeur sur tablette. */

const PHASES: Phase[] = ["T1", "T2", "T3"];

const FACING: Record<string, string> = {
  Reconnaissance: k("à la reconnaissance"),
  "Premier échelon": k("au premier échelon"),
  "Second échelon": k("au second échelon"),
  Appuis: k("aux appuis"),
};
const COMPASS: Record<string, string> = {
  N: k("le nord"),
  NE: k("le nord-est"),
  E: k("l'est"),
  SE: k("le sud-est"),
  S: k("le sud"),
  SO: k("le sud-ouest"),
  O: k("l'ouest"),
  NO: k("le nord-ouest"),
};

/** Échelon ennemi en face : « face au premier échelon »… ; un rôle hors liste garde son libellé. */
function facing(label: string): string {
  const text = FACING[label];
  return text ? t(text) : tServer(label).toLowerCase();
}

/** Direction de l'effort ennemi : « vers le nord-est »… ; un point cardinal hors liste reste tel quel. */
function direction(compass: string): string {
  const text = COMPASS[compass];
  return text ? t(text) : compass;
}

const km = (m: number) => `${(m / 1000).toLocaleString(locale(), { maximumFractionDigits: 1 })} km`;

function ratioText(ratio: number): string {
  const n = (v: number) => v.toLocaleString(locale(), { maximumFractionDigits: 1 });
  return ratio >= 1 ? t("{n} contre 1", { n: n(ratio) }) : t("1 contre {n}", { n: n(1 / ratio) });
}

/** RAPFOR exigé par la doctrine, tel que le serveur l'écrit (« 3/1 », « 1 à 1,5/1 ») : décimales et fourchette
 *  dans la langue de l'interface. */
function doctrineText(text: string): string {
  return text
    .replace(/\d+(?:,\d+)?/g, (value) => Number(value.replace(",", ".")).toLocaleString(locale()))
    .replace(/(\S+) à (\S+)/g, (_, min: string, max: string) => t("{min} à {max}", { min, max }));
}

function Confidence({ value }: { value: number | null }) {
  if (!value) return null;
  return (
    <span className="flex items-center gap-1" title={t("Confiance {n} sur 5", { n: value })}>
      {[1, 2, 3, 4, 5].map((i) => (
        <span key={i} className={clsx("h-1.5 w-3 rounded-full", i <= value ? "bg-accent" : "bg-line-strong")} />
      ))}
    </span>
  );
}

/** Évaluation de l'IA : l'état de chaque rubrique est porté par une icône colorée (ok, danger) à côté du libellé
 *  et par les puces ; la recommandation est en accent. */
function Assessment({ value }: { value: WargameAssessment }) {
  return (
    <div className={clsx("space-y-3 text-title leading-relaxed text-ink", motion("fade"))}>
      {value.narrative && <p className="text-justify hyphens-auto">{value.narrative}</p>}
      {value.strengths.length > 0 && (
        <div>
          <SectionLabel as="h3" inset className="mb-1 flex items-center gap-1">
            <CheckCircle2 size={12} aria-hidden className="shrink-0 text-ok" />
            {t("Points forts")}
          </SectionLabel>
          <ul className="space-y-1">
            {value.strengths.map((s) => (
              <li key={s} className="flex gap-2"><span className="text-ok">•</span><span>{s}</span></li>
            ))}
          </ul>
        </div>
      )}
      {value.vulnerabilities.length > 0 && (
        <div>
          <SectionLabel as="h3" inset className="mb-1 flex items-center gap-1">
            <AlertTriangle size={12} aria-hidden className="shrink-0 text-danger" />
            {t("Vulnérabilités")}
          </SectionLabel>
          <ul className="space-y-1">
            {value.vulnerabilities.map((s) => (
              <li key={s} className="flex gap-2"><span className="text-danger">•</span><span>{s}</span></li>
            ))}
          </ul>
        </div>
      )}
      {value.recommendation && (
        <div className="rounded-lg bg-accent-soft px-3 py-2">
          <SectionLabel as="h3" inset tone="accent" className="mb-0.5">{t("Recommandation")}</SectionLabel>
          <p>{value.recommendation}</p>
        </div>
      )}
    </div>
  );
}

const FLIGHTS: Record<DroneRegime, string> = {
  libre: k("vols libres"),
  sur_ordre: k("vols sur ordre"),
  interdit: k("vols interdits"),
};

/** Liste « 1, 2 et 3 » dans la langue de l'interface. */
function listText(items: string[]): string {
  return new Intl.ListFormat(locale(), { style: "long", type: "conjunction" }).format(items);
}

/** Bloc « Drones » des constats calculés (chantier « Drones ») au temps affiché : régime de nos vols, unités amies à
 *  portée des drones FPV ennemis (désignations en infobulle), NAI dans le rayon de nos drones, puis l'état des petits
 *  drones quand la météo les gêne. Affichage seul : la consigne de l'IA ne le reçoit pas. */
export function DronesFacts({ drones, phase }: { drones: WargameDrones; phase: Phase }) {
  const facts = drones.phases[phase];
  if (!facts) return null;
  const parts = [t(FLIGHTS[facts.regime])];
  if (facts.exposed.length > 0)
    parts.push(t("{units} à portée des drones FPV ennemis", { units: plural(facts.exposed.length, t("unité"), t("unités")) }));
  if (facts.nai_in_range.length > 0)
    parts.push(t("NAI {list} dans le rayon de nos drones", { list: listText(facts.nai_in_range.map(String)) }));
  const small = smallDronesEffect(drones.small_drones);
  return (
    <>
      <p className="flex gap-2 text-ui leading-snug text-ink" title={facts.exposed.length > 0 ? facts.exposed.map(unitDesignation).join(", ") : undefined}>
        <Drone size={14} className="mt-0.5 shrink-0 text-ink-muted" />
        <span>{t("Drones en {phase} : {facts}.", { phase, facts: parts.join(t(" ; ")) })}</span>
      </p>
      {small && (
        <p className="flex gap-2 text-ui leading-snug text-ink">
          <AlertTriangle size={14} className="mt-0.5 shrink-0 text-warn" />
          <span>{small}</span>
        </p>
      )}
    </>
  );
}

/** Dernier wargame d'un dossier (et de son OPO en CENTAC), gardé pour la session (audit de persistance m9) : quitter
 *  la vue ou fermer le volet ne perd plus la simulation ni l'évaluation de l'IA. Non gardé après un rechargement ;
 *  une évaluation interrompue (vue quittée pendant la rédaction) garde sa simulation. Modes d'action ennemis
 *  changés depuis (`basis`) : rien n'est rétabli ; sinon le résultat rétabli dit l'heure de son calcul, à relancer
 *  si le dossier a changé (ordre de bataille, dispositif, manœuvre amie). */
interface WargameMemory {
  basis: string;
  at: number;
  bloc: 1 | 2;
  sim: WargameSimulation | null;
  raw: string;
  source: string | null;
  assessment: WargameAssessment | null;
  assessmentError: string | null;
  error: string | null;
}
const memories = new Map<string, WargameMemory>();

export function WargamePanel({
  exerciseId,
  opo,
  me1,
  me2,
  phase,
  onPhase,
  onSimulation,
  onClose,
}: {
  exerciseId: string;
  opo: number | undefined;
  me1: CoaScenario | null;
  me2: CoaScenario | null;
  phase: SketchPhase;
  onPhase: (phase: Phase) => void;
  onSimulation: (sim: WargameSimulation | null) => void;
  onClose: () => void;
}) {
  const memoryKey = `${exerciseId}:${opo ?? 1}`;
  const basis = JSON.stringify([me1, me2]);
  const [kept] = useState(() => {
    const memory = memories.get(memoryKey);
    return memory?.basis === basis ? memory : undefined;
  });
  // Heure du calcul rétabli (null : calculé dans ce volet, ou rien de rétabli).
  const [restoredAt, setRestoredAt] = useState<number | null>(kept?.sim ? kept.at : null);
  const computedAt = useRef(kept?.at ?? Date.now());
  const [bloc, setBloc] = useState<1 | 2>(kept?.bloc ?? 1);
  const [running, setRunning] = useState(false);
  const [sim, setSim] = useState<WargameSimulation | null>(kept?.sim ?? null);
  const [raw, setRaw] = useState(kept?.raw ?? "");
  const [source, setSource] = useState<string | null>(kept?.source ?? null);
  const [assessment, setAssessment] = useState<WargameAssessment | null>(kept?.assessment ?? null);
  const [assessmentError, setAssessmentError] = useState<string | null>(kept?.assessmentError ?? null);
  const [error, setError] = useState<string | null>(kept?.error ?? null);
  const abort = useRef<AbortController | null>(null);

  useEffect(() => () => abort.current?.abort(), []);
  // Simulation gardée : le croquis la montre de nouveau.
  useEffect(() => {
    if (kept?.sim) onSimulation(kept.sim);
  }, []);
  useEffect(() => {
    memories.set(memoryKey, { basis, at: computedAt.current, bloc, sim, raw, source, assessment, assessmentError, error });
  }, [memoryKey, basis, bloc, sim, raw, source, assessment, assessmentError, error]);

  const run = async () => {
    abort.current?.abort();
    const controller = new AbortController();
    abort.current = controller;
    computedAt.current = Date.now();
    setRestoredAt(null);
    setRunning(true);
    setSim(null);
    onSimulation(null);
    setRaw("");
    setSource(null);
    setAssessment(null);
    setAssessmentError(null);
    setError(null);
    try {
      await streamWargame(
        exerciseId,
        bloc,
        opo,
        ({ event, data }) => {
          if (event === "simulation") {
            const result = data as WargameSimulation;
            setSim(result);
            onSimulation(result);
            if (result.peak) onPhase(result.peak);
          } else if (event === "source") setSource(data.label ?? null);
          else if (event === "token") setRaw((r) => r + (data.text ?? ""));
          else if (event === "assessment") {
            setAssessment(data.assessment ?? null);
            if (data.raw) setRaw(data.raw);
            setAssessmentError(data.error ?? null);
          }
        },
        controller.signal,
      );
    } catch (e) {
      if (!controller.signal.aborted) setError(e instanceof Error ? e.message : String(e));
    } finally {
      if (abort.current === controller) setRunning(false);
    }
  };

  const selected: Phase = phase === "all" ? (sim?.peak ?? "T1") : phase;
  const data = sim?.phases[selected];
  const maxIntensity = sim ? Math.max(1, ...PHASES.map((p) => sim.phases[p].intensity)) : 1;
  const total = sim ? PHASES.reduce((n, p) => n + sim.phases[p].contacts.length, 0) : 0;
  const choices: { bloc: 1 | 2; scenario: CoaScenario | null; label: string }[] = [
    { bloc: 1, scenario: me1, label: t("Le plus probable") },
    { bloc: 2, scenario: me2, label: t("Le plus dangereux") },
  ];
  const choiceEnabled = choices.map((c) => Boolean(c.scenario) && !running);

  return (
    <SidePanel title={t("Simulation des ME")} icon={Play} onClose={onClose}>
      <section className="space-y-3 border-b border-line px-4 py-3.5">
        <p className="text-ui leading-relaxed text-ink-soft">
          {t("Confronte la manœuvre amie, temps par temps, à un mode d'action ennemi : contacts, sévérités, feux et rapport de forces sont calculés ; l'IA rédige ensuite l'évaluation.")}
        </p>
        <div
          className="space-y-1.5"
          role="radiogroup"
          aria-label={t("Mode d'action ennemi confronté")}
          onKeyDown={(e) => onRadioGroupKeyDown(e, (i) => setBloc(choices[i]!.bloc))}
        >
          {choices.map((c, i) => (
            <ChoiceCard
              key={c.bloc}
              radio
              selected={bloc === c.bloc}
              tabIndex={rovingTabIndex(i, choices.findIndex((o) => o.bloc === bloc), choiceEnabled)}
              disabled={!c.scenario || running}
              onClick={() => setBloc(c.bloc)}
              title={
                <span className="flex items-center gap-2">
                  <Badge tone="hostile" variant="solid" size="sm">{t("ME{n}", { n: c.bloc })}</Badge>
                  <span className="min-w-0 truncate">{c.scenario?.name ?? "—"}</span>
                </span>
              }
              detail={c.label}
            />
          ))}
        </div>
        <Button variant={sim ? "secondary" : "primary"} className="w-full" highlight={!sim && !running} onClick={run} disabled={running}>
          {running ? <Loader2 size={14} className="animate-spin" /> : sim ? <RotateCcw size={14} /> : <Play size={14} />}
          {running
            ? sim
              ? <Waiting>{t("Évaluation par l'IA…")}</Waiting>
              : <Waiting>{t("Simulation…")}</Waiting>
            : sim
              ? t("Nouvelle simulation")
              : t("Lancer la simulation")}
        </Button>
        {restoredAt !== null && sim && !running && (
          <p className="text-meta leading-relaxed text-ink-muted">
            {t("Dernière simulation des ME de la session, calculée à {time} : relancez-la si l'ordre de bataille, le dispositif ou la manœuvre amie ont changé depuis.", {
              time: new Date(restoredAt).toLocaleTimeString(locale(), { hour: "2-digit", minute: "2-digit" }),
            })}
          </p>
        )}
        <Presence show={Boolean(error)}>{error && <Callout tone="danger">{error}</Callout>}</Presence>
      </section>

      {sim && data && (
        <div className={motion("fade")}>
          <section className="grid grid-cols-3 gap-2 border-b border-line px-4 py-3.5">
            <StatTile label={t("Pic")} value={sim.peak ?? "—"} hint={sim.peak ? t("temps le plus intense") : t("aucun contact")} />
            <StatTile label={t("Contacts")} value={total} hint={t("sur les trois temps")} />
            <StatTile
              label={t("RAPFOR")}
              value={sim.rapfor.ratio === null ? "—" : ratioText(sim.rapfor.ratio)}
              tone={sim.rapfor.favorable === null ? "neutral" : sim.rapfor.favorable ? "ok" : "warn"}
              hint={
                sim.rapfor.doctrine
                  ? t("exigé {ratio} : {result}", {
                      ratio: doctrineText(sim.rapfor.doctrine.text),
                      result: sim.rapfor.favorable ? t("atteint") : t("non atteint"),
                    })
                  : t("sans exigence doctrinale")
              }
            />
          </section>

          <section className="border-b border-line px-4 py-3.5">
            <div className="grid grid-cols-3 gap-2" role="tablist" aria-label={t("Temps de la confrontation")}>
              {PHASES.map((p) => {
                const ph = sim.phases[p];
                return (
                  <button
                    key={p}
                    type="button"
                    role="tab"
                    aria-selected={selected === p}
                    onClick={() => onPhase(p)}
                    className={clsx(
                      "rounded-lg border px-2.5 py-2 text-left transition-colors",
                      selected === p ? "border-accent bg-accent-soft" : "border-line hover:bg-hover",
                    )}
                  >
                    <span className="flex items-center justify-between gap-1">
                      <span className="font-mono text-ui font-semibold text-ink">{p}</span>
                      {sim.peak === p && (
                        <Badge size="sm" tone="hostile">
                          {t("Pic")}
                        </Badge>
                      )}
                    </span>
                    <span className="mt-1.5 block h-1.5 overflow-hidden rounded-full bg-canvas ring-1 ring-line">
                      <span
                        className="block h-full rounded-full bg-hostile/80 transition-[width] duration-(--gx-enter)"
                        style={{ width: `${(ph.intensity / maxIntensity) * 100}%` }}
                      />
                    </span>
                    <span className="mt-1 block text-meta text-ink-muted">{plural(ph.contacts.length, t("contact"), t("contacts"))}</span>
                  </button>
                );
              })}
            </div>
            <div className="mt-3 flex flex-wrap gap-1.5">
              {SEVERITY_ORDER.map((s) => (
                <Chip key={s} icon={<span className="size-2 rounded-full" style={{ background: SEVERITY_COLOR[s] }} />}>
                  {t(SEVERITY_LABEL[s])} <span className="font-semibold text-ink tabular-nums">{sim.severity_counts[s]}</span>
                </Chip>
              ))}
            </div>
          </section>

          <section className="border-b border-line">
            <SectionLabel as="h3" inset className="px-4 pt-3 pb-1.5">
              {t("Contacts en {phase}", { phase: selected })}
            </SectionLabel>
            {data.contacts.length === 0 ? (
              <p className="px-4 pb-3 text-ui text-ink-muted">{t("Aucun contact à portée d'engagement.")}</p>
            ) : (
              <ul className="divide-y divide-line">
                {data.contacts.map((c) => (
                  <li key={`${c.ami}-${c.eni_role}`} className="flex min-h-8 gap-2.5 px-4 py-2">
                    <span className="mt-1 size-2.5 shrink-0 rounded-full" style={{ background: SEVERITY_COLOR[c.severity] }} />
                    <span className="min-w-0 flex-1">
                      <span className="block text-ui leading-snug text-ink">
                        <span className="font-semibold">{unitDesignation(c.ami)}</span>
                        <span className="text-ink-muted"> {t("face")} </span>
                        <span className="font-semibold text-hostile">{facing(c.eni_label)}</span>
                      </span>
                      <span className="block text-meta leading-snug text-ink-muted" title={c.eni_units.map(unitDesignation).join(", ")}>
                        {c.distance_m === 0 ? t("Au contact direct") : t("À {distance}", { distance: km(c.distance_m) })}
                        {" · "}
                        {t("ENI {posture}", { posture: tServer(c.eni_posture).toLowerCase() })}
                        {c.ami_mission ? ` · ${t("ami : {mission}", { mission: unitMissionName(c.ami_mission) })}` : ""}
                        {!c.eni_in_range
                          ? ` · ${t("hors de portée ennemie")}`
                          : !c.ami_in_range
                            ? ` · ${t("hors de portée amie")}`
                            : ""}
                      </span>
                    </span>
                    <span className="shrink-0 text-meta font-semibold" style={{ color: SEVERITY_COLOR[c.severity] }}>
                      {tServer(c.severity_label)}
                    </span>
                  </li>
                ))}
              </ul>
            )}
            <div className="space-y-1.5 px-4 pt-1 pb-3">
              {data.fires.length > 0 && (
                <p className="flex gap-2 text-meta leading-snug text-ink-soft">
                  <Flame size={14} className="mt-0.5 shrink-0 text-hostile" />
                  <span>
                    {t("Sous les feux des appuis ennemis : {units}.", { units: data.fires.map((f) => unitDesignation(f.ami)).join(", ") })}
                  </span>
                </p>
              )}
            </div>
          </section>

          <section className="space-y-2 border-b border-line px-4 py-3.5">
            <SectionLabel as="h3" inset>{t("Constats calculés")}</SectionLabel>
            <p className="flex gap-2 text-ui leading-snug text-ink">
              <Compass size={14} className="mt-0.5 shrink-0 text-ink-muted" />
              {sim.effort
                ? t("Effort ennemi de T1 à T3 vers {direction}, sur {distance}, {heading}.", {
                    direction: direction(sim.effort.compass),
                    distance: km(sim.effort.distance_m),
                    heading: sim.effort.toward_friends ? t("en direction des amis") : t("en s'éloignant des amis"),
                  })
                : t("Échelons ennemis quasi statiques de T1 à T3.")}
            </p>
            {sim.findings.map((f) => (
              <p key={f.kind + f.text} className="flex gap-2 text-ui leading-snug text-ink">
                {f.tone === "ok" ? (
                  <CheckCircle2 size={14} className="mt-0.5 shrink-0 text-ok" />
                ) : (
                  <AlertTriangle size={14} className="mt-0.5 shrink-0 text-warn" />
                )}
                {tServer(f.text)}
              </p>
            ))}
            {sim.drones && <DronesFacts drones={sim.drones} phase={selected} />}
          </section>

          <section className="px-4 py-3.5">
            <SectionLabel
              as="h3"
              inset
              className="mb-2"
              trailing={<Confidence value={assessment?.confidence ?? null} />}
            >
              <span className="inline-flex items-center gap-2">
                <Sparkles size={14} className="text-run" />
                {t("Évaluation par l'IA")}
              </span>
            </SectionLabel>
            {assessment ? (
              <Assessment value={assessment} />
            ) : assessmentError ? (
              <p className="text-ui leading-relaxed text-ink-soft">
                {tServer(assessmentError)} {t("Les résultats calculés ci-dessus restent valables.")}
              </p>
            ) : raw ? (
              <p className="text-ui leading-relaxed whitespace-pre-wrap text-ink-soft">
                {raw}
                {running && <span className="briefing-caret" />}
              </p>
            ) : running ? (
              <p className="flex items-center gap-2 text-ui text-ink-muted">
                <Loader2 size={14} className="animate-spin" /> {<Waiting>{t("L'IA prend connaissance des faits…")}</Waiting>}
              </p>
            ) : (
              <p className="text-ui text-ink-muted">{t("Réponse de l'IA inexploitable : relancez la simulation.")}</p>
            )}
            {source && (
              <p className="mt-3 text-meta text-ink-muted">
                {t("Rédigée par le {source}, d'après les faits calculés : à valider par le chef.", { source: tServer(source) })}
              </p>
            )}
          </section>
        </div>
      )}
    </SidePanel>
  );
}
